<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="warehouses"
      :headers="tableHeaders"
      :list="warehouses"
      table-id="warehouses"
      clickable
      @table-item-clicked="onEdit"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="refreshData"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div v-if="item.isFromManager" class="flex items-center justify-center gap-1">
          <button
            type="button"
            class="rounded-full p-1.5 text-red-400 hover:bg-red-50"
            @click="onDeleteRow(item as Warehouse)">
            <IconTrashOutline class="size-4" />
          </button>
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <WarehouseModalCreateEdit
      v-if="createEditModalActive"
      :warehouse="warehouse"
      :mode="createEditModalMode"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import { v4 as uuidv4 } from 'uuid';
import { removeWarehousesByIds, getWarehouses } from '@/services/api/materials';

import BaseTable from '@/components/common/BaseTable.vue';
import WarehouseModalCreateEdit from '@/components/warehouses/WarehouseModalCreateEdit.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import { type Warehouse } from '@/types/Warehouse';
import type { TableFilterData } from '@/types/Base';
import { filterActive, filterData, sortData } from '@/utils/table';
import { useI18n } from 'vue-i18n';
import { useLookupsStore } from '@/stores/lookups';

const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();
const { reloadLookups } = useLookupsStore();

const warehouses = ref<Warehouse[]>();
const filterIsActive = ref<boolean | undefined>();

const tableHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'companySite.name', text: $t('traderSites.companySite'), type: 'text', active: true },
  { value: 'address', text: $t('address'), type: 'text', active: true },
  { value: 'city', text: $t('city'), type: 'text', active: true },
  { value: 'isLogistics', text: $t('warehouse.isLogistics'), type: 'check', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

function initializeWarehouse(): Warehouse {
  return {
    id: uuidv4(),
    code: '',
    name: '',
    address: '',
    city: '',
    companySiteId: '',
    isLogistics: false,
    isFromManager: true,
    isActive: false,
  };
}

onMounted(async () => {
  const res = await getWarehouses();
  if (res) warehouses.value = res;
});

async function onDeleteMultiselect(items: Warehouse[]) {
  const itemsArray = Array.isArray(items) ? items : [items];
  const itemsToDelete = itemsArray.filter((item) => item.isFromManager);
  if (itemsToDelete.length === 0) {
    toastStore.error({ text: 'Selected items cannot be deleted.' });
    return;
  }

  const idsToDelete = itemsToDelete.map((item) => item.id);
  const res = await removeWarehousesByIds(idsToDelete);
  if (!res && warehouses.value)
    warehouses.value = warehouses.value.filter((warehouse) => !idsToDelete.includes(warehouse.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['warehouses']);
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeWarehousesByIds([idToDelete]);
  if (!res && warehouses.value)
    warehouses.value = warehouses.value.filter((warehouse) => warehouse.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['warehouses']);
}

const warehouse = ref<Warehouse>(initializeWarehouse());

const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');

function onAddNew() {
  warehouse.value = initializeWarehouse();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEdit(item: Warehouse) {
  warehouse.value = item;
  createEditModalMode.value = 'edit';
  createEditModalActive.value = true;
}

async function onSave(modifiedData: Warehouse) {
  if (!warehouses.value) return;

  const index = warehouses.value.findIndex((item) => item.id === modifiedData.id);

  if (index !== -1) warehouses.value[index] = modifiedData;
  else warehouses.value.push(modifiedData);
  createEditModalActive.value = false;

  await reloadLookups(['warehouses']);
}

async function refreshData(data: TableFilterData) {
  let processedData = await getWarehouses();

  if (data.query) {
    processedData = filterData(processedData, data.query, ['name', 'code', 'address', 'city']);
  }

  if (data.column !== undefined && data.order) {
    processedData = sortData(processedData, data.column, data.order, tableHeaders);
  }

  if (data.isActive !== undefined) processedData = filterActive(processedData, data.isActive);

  warehouses.value = processedData;
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<Warehouse>();
function onDeleteRow(item: Warehouse) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  refreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  refreshData({});
}
</script>
